import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { Box, Tooltip, Typography, Stack, Popover, IconButton, Button } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import PaletteIcon from '@mui/icons-material/Palette';
import { isValidHexColor, DEFAULT_SWATCH_COLOR } from '../utils/colorValidation';
import { CHART_COLOR_PALETTE, COLOR_NAMES } from '../utils/colorUtils';

/**
 * Color picker component for question options.
 * Allows users to select custom hex colors for report visualization.
 *
 * @param value - Current hex color value (e.g., "#1976d2")
 * @param onChange - Callback when color changes
 * @param disabled - Whether the picker is disabled
 * @param label - Accessible label for the picker
 * @param optionIndex - Option index for ARIA labels (optional)
 *
 * @example
 * <OptionColorPicker
 *   value={option.color}
 *   onChange={(color) => updateColor(option.id, color)}
 *   label="Chart color"
 *   optionIndex={0}
 * />
 */
interface OptionColorPickerProps {
  value?: string;
  onChange: (color: string | undefined) => void;
  disabled?: boolean;
  label?: string;
  optionIndex?: number;
}

/**
 * Individual color swatch button for preset colors
 */
interface ColorSwatchButtonProps {
  color: string;
  isSelected: boolean;
  onClick: (color: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  tabIndex: number;
}

const ColorSwatchButton: React.FC<ColorSwatchButtonProps> = ({
  color,
  isSelected,
  onClick,
  onKeyDown,
  tabIndex,
}) => {
  const colorName = COLOR_NAMES[color] || color;
  const ariaLabel = `${colorName} ${color}`;

  return (
    <Tooltip title={`${colorName} (${color})`} arrow placement="top">
      <IconButton
        onClick={() => onClick(color)}
        onKeyDown={onKeyDown}
        tabIndex={tabIndex}
        role="menuitem"
        aria-label={ariaLabel}
        sx={{
          width: 40,
          height: 40,
          padding: 0,
          backgroundColor: color,
          border: '2px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
          borderRadius: '4px',
          position: 'relative',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: color,
          },
          '&:focus': {
            borderColor: 'primary.main',
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px',
          },
        }}
      >
        {isSelected && (
          <CheckIcon
            sx={{
              color: 'white',
              fontSize: '20px',
              filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))',
            }}
          />
        )}
      </IconButton>
    </Tooltip>
  );
};

/**
 * Grid of preset color swatches with keyboard navigation
 */
interface PresetColorGridProps {
  currentColor?: string;
  onColorSelect: (color: string) => void;
  onCustomClick: () => void;
  onClose: () => void;
}

const PresetColorGrid: React.FC<PresetColorGridProps> = ({
  currentColor,
  onColorSelect,
  onCustomClick,
  onClose,
}) => {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);
  const customButtonRef = useRef<HTMLButtonElement>(null);

  // Grid dimensions: 4 columns x 3 rows
  const GRID_COLS = 4;
  const GRID_ROWS = 3;

  // Focus first preset color on mount
  useEffect(() => {
    if (gridRef.current) {
      const firstButton = gridRef.current.querySelector('button');
      if (firstButton) {
        (firstButton as HTMLButtonElement).focus();
      }
    }
  }, []);

  // Handle 2D arrow key navigation
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const row = Math.floor(index / GRID_COLS);
    const col = index % GRID_COLS;

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        if (col < GRID_COLS - 1) {
          setFocusedIndex(index + 1);
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (col > 0) {
          setFocusedIndex(index - 1);
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (row < GRID_ROWS - 1) {
          setFocusedIndex(index + GRID_COLS);
        } else {
          // Move to custom color button
          customButtonRef.current?.focus();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (row > 0) {
          setFocusedIndex(index - GRID_COLS);
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        onColorSelect(CHART_COLOR_PALETTE[index]);
        break;
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
      case 'Tab':
        // Allow natural tab navigation to custom button
        if (!event.shiftKey) {
          event.preventDefault();
          customButtonRef.current?.focus();
        }
        break;
      default:
        break;
    }
  };

  const handleCustomButtonKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        // Move focus to last row of grid
        setFocusedIndex(CHART_COLOR_PALETTE.length - 1);
        break;
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        onCustomClick();
        break;
      default:
        break;
    }
  };

  // Update focused button when index changes
  useEffect(() => {
    if (gridRef.current) {
      const buttons = gridRef.current.querySelectorAll('button');
      if (buttons[focusedIndex]) {
        (buttons[focusedIndex] as HTMLButtonElement).focus();
      }
    }
  }, [focusedIndex]);

  return (
    <Box
      role="menu"
      aria-label="Color preset picker"
      sx={{
        padding: 2,
        width: 220,
      }}
    >
      {/* Preset Color Grid */}
      <Box
        ref={gridRef}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 40px)',
          gap: 1,
          mb: 2,
        }}
      >
        {CHART_COLOR_PALETTE.map((color, index) => (
          <ColorSwatchButton
            key={color}
            color={color}
            isSelected={currentColor?.toUpperCase() === color.toUpperCase()}
            onClick={onColorSelect}
            onKeyDown={(event) => handleKeyDown(event, index)}
            tabIndex={index === focusedIndex ? 0 : -1}
          />
        ))}
      </Box>

      {/* Custom Color Button */}
      <Button
        ref={customButtonRef}
        onClick={onCustomClick}
        onKeyDown={handleCustomButtonKeyDown}
        fullWidth
        variant="outlined"
        size="small"
        startIcon={<PaletteIcon />}
        sx={{
          justifyContent: 'flex-start',
          textTransform: 'none',
        }}
      >
        Custom Color...
      </Button>
    </Box>
  );
};

export const OptionColorPicker: React.FC<OptionColorPickerProps> = ({
  value,
  onChange,
  disabled = false,
  label = 'Chart color',
  optionIndex,
}) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Sync local value when prop changes
  useEffect(() => {
    setLocalValue(value || '');
    if (value && !isValidHexColor(value)) {
      setError('Invalid hex color format');
    } else {
      setError(null);
    }
  }, [value]);

  const handleColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Don't process changes when disabled
    if (disabled) return;

    const newColor = event.target.value;
    setLocalValue(newColor);

    // Native color input always returns valid hex color
    if (isValidHexColor(newColor)) {
      setError(null);
      onChange(newColor);
    }
  };

  const handleSwatchClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    // Return focus to trigger
    triggerRef.current?.focus();
  };

  const handlePresetColorSelect = (color: string) => {
    setLocalValue(color);
    setError(null);
    onChange(color);
    handlePopoverClose();

    // Announce color change for screen readers
    const colorName = COLOR_NAMES[color] || color;
    const announcement = `Color changed to ${colorName} ${color}`;
    announceToScreenReader(announcement);
  };

  const handleCustomColorClick = () => {
    handlePopoverClose();
    // Trigger native color picker
    if (colorInputRef.current) {
      colorInputRef.current.click();
    }
  };

  const ariaLabel = optionIndex !== undefined ? `${label} for option ${optionIndex + 1}` : label;

  const tooltipTitle = value ? `Custom color: ${value}` : 'Custom chart color (optional)';

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ minWidth: '60px' }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {/* Color Swatch Trigger */}
        <Tooltip title={tooltipTitle} arrow placement="top">
          <Box
            ref={triggerRef}
            onClick={handleSwatchClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSwatchClick(e as unknown as React.MouseEvent<HTMLElement>);
              }
            }}
            tabIndex={disabled ? -1 : 0}
            role="button"
            aria-label={ariaLabel}
            aria-expanded={open}
            aria-haspopup="menu"
            sx={{
              position: 'relative',
              width: 32,
              height: 32,
              border: '2px solid',
              borderColor: error ? 'error.main' : 'divider',
              borderRadius: '4px',
              overflow: 'hidden',
              cursor: disabled ? 'not-allowed' : 'pointer',
              backgroundColor: localValue || DEFAULT_SWATCH_COLOR,
              '&:hover': {
                borderColor: disabled ? 'divider' : 'primary.main',
              },
              '&:focus': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: '2px',
              },
            }}
          />
        </Tooltip>

        {/* Hidden Native Color Input (for custom colors) */}
        <input
          ref={colorInputRef}
          type="color"
          value={localValue || DEFAULT_SWATCH_COLOR}
          onChange={handleColorChange}
          disabled={disabled}
          aria-hidden="true"
          style={{
            position: 'absolute',
            opacity: 0,
            width: 0,
            height: 0,
            pointerEvents: 'none',
          }}
        />
      </Stack>

      {/* Preset Color Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              mt: 1,
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
              },
            },
          },
        }}
      >
        <PresetColorGrid
          currentColor={localValue}
          onColorSelect={handlePresetColorSelect}
          onCustomClick={handleCustomColorClick}
          onClose={handlePopoverClose}
        />
      </Popover>

      {/* Error Message */}
      {error && (
        <Typography
          variant="caption"
          color="error"
          sx={{
            display: 'block',
            mt: 0.5,
            fontSize: '0.65rem',
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Utility function to announce messages to screen readers
 * Creates a live region announcement that is automatically removed
 */
function announceToScreenReader(message: string): void {
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', 'polite');
  announcement.setAttribute('aria-atomic', 'true');
  announcement.style.position = 'absolute';
  announcement.style.left = '-10000px';
  announcement.style.width = '1px';
  announcement.style.height = '1px';
  announcement.style.overflow = 'hidden';
  announcement.textContent = message;

  document.body.appendChild(announcement);

  // Remove after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
}
